import { FileSignature, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useTdrs } from '@/api/hooks';
import { LIBELLE_STATUT, type StatutDocument } from '@/api/types';
import { peutEmettreDocuments } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, EtapeBadge, PageHeader, StatutBadge, Vide } from '@/components/common';
import { BandeauxACorriger, EntonnoirDocuments, niveauDocument, type CleNiveau } from '@/components/documents';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFiltresListe } from '@/lib/filtres-liste';
import { dateFr, montant } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT) as StatutDocument[];

export function TdrListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const peutEmettre = peutEmettreDocuments(u.habilitations);
  const valideur = u.etapes.length > 0;
  const { onglet, statut, setOnglet, setStatut } = useFiltresListe(valideur ? (['a-traiter', 'tous'] as const) : (['tous'] as const), valideur ? 'a-traiter' : 'tous', 'tous');
  const { data, isLoading, error } = useTdrs(onglet === 'a-traiter' ? { aTraiter: true } : statut === 'TOUS' ? {} : { statut });
  // « Où en sont mes TDR ? » (maquette émetteur) : résumé par niveau du circuit, filtre au clic, bandeaux « à corriger ».
  const [niveau, setNiveau] = useState<CleNiveau | null>(null);
  const miens = (data ?? []).filter((t) => t.emetteur?.id === u.agentId);
  const resume = miens.map((t) => ({ id: t.id, numero: t.numero, titre: t.titre, statut: t.statut, etapeCourante: t.etapeCourante, montant: t.montant, correction: t.correction }));
  const visibles = niveau ? (data ?? []).filter((t) => niveauDocument(t) === niveau) : (data ?? []);
  return (
    <div>
      <PageHeader
        titre="Termes de référence"
        sousTitre="Missions, formations et autres activités soumises à validation."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/ne">
                <FileSignature className="size-4" /> Notes explicatives
              </Link>
            </Button>
            {peutEmettre && (
              <>
                {/* Note explicative sur un TDR déjà soumis ou autorisé (GEDEM v3.0, § 7) : le TDR se choisit à l'étape suivante. */}
                <Button asChild variant="outline">
                  <Link to="/ne/nouvelle">
                    <Plus className="size-4" /> Nouvelle note
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/tdr/nouveau">
                    <Plus className="size-4" /> Nouveau TDR
                  </Link>
                </Button>
              </>
            )}
          </>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={onglet} onValueChange={(v) => setOnglet(v as 'a-traiter' | 'tous')}>
          <TabsList>
            {valideur && <TabsTrigger value="a-traiter">À traiter</TabsTrigger>}
            <TabsTrigger value="tous">{u.habilitations.some((h) => h.structureId === null) ? 'Tous les TDR' : 'Mes TDR et ceux de ma structure'}</TabsTrigger>
          </TabsList>
        </Tabs>
        {onglet === 'tous' && (
          <Select value={statut} onValueChange={(v) => setStatut(v as StatutDocument | 'TOUS')}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TOUS">Tous les statuts</SelectItem>
              {STATUTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {LIBELLE_STATUT[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {peutEmettre && onglet === 'tous' && statut === 'TOUS' && resume.length > 0 && (
        <div className="mb-4 space-y-3">
          <BandeauxACorriger documents={resume} chemin={(x) => `/tdr/${x}`} libelle="TDR" />
          <EntonnoirDocuments libelle="TDR" documents={resume} etapes={['DIRECTEUR', 'COORDONNATEUR_MEMORIAL', 'CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_GENERAL']} selection={niveau} onSelection={setNiveau} />
        </div>
      )}
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !visibles.length ? (
        <Vide message={onglet === 'a-traiter' ? 'Aucun TDR en attente de votre décision.' : niveau ? 'Aucun TDR à ce niveau.' : 'Aucun TDR.'} />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead className="hidden md:table-cell">Structure</TableHead>
                <TableHead className="hidden lg:table-cell">Émetteur</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="hidden sm:table-cell">Mis à jour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    <Link to={`/tdr/${t.id}`} className="font-medium text-primary hover:underline">
                      {t.numero ?? 'Brouillon'}
                    </Link>
                    {t.urgence && <span className="ml-1 text-xs text-red-700">urgent</span>}
                  </TableCell>
                  <TableCell className="max-w-[36ch] truncate">{t.titre}</TableCell>
                  <TableCell className="hidden md:table-cell">{t.structureCode}</TableCell>
                  <TableCell className="hidden lg:table-cell">{t.emetteur?.nom}</TableCell>
                  <TableCell className="num text-right">{montant(t.montant)}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutBadge statut={t.statut} />
                    {t.statut === 'SOUMIS' && <EtapeBadge etape={t.etapeCourante} />}
                    {t.correction && <span className="text-xs text-amber-700">avant le {dateFr(t.correction.dateLimite)}</span>}
                    {t.annulationAutomatique && <span className="text-xs text-muted-foreground">sans réponse</span>}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{dateFr(t.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
